import React, { useState, useEffect, useRef } from 'react';
import { db } from '../firebaseConfig';
import { useAuth } from '../context/AuthContext';
import { collection, addDoc, onSnapshot, query, orderBy, serverTimestamp, Timestamp, updateDoc, doc, arrayUnion, arrayRemove, deleteDoc, getDoc, setDoc, increment } from 'firebase/firestore';
import { MapPin, Video, Heart, Trash2, X, Plus, Search, RotateCw, MessageCircle, Trophy, Flame } from 'lucide-react';
import './Goller.css';
import { turkeyCities as TURKEY_CITIES } from '../data/turkeyCities';
import { pushNotification } from '../utils/notify';
import { uploadMedia } from '../utils/uploadMedia';

// Haftanın başı (Pazartesi 00:00)
const getWeekStart = () => {
  const now = new Date();
  const day = now.getDay(); // 0=Pazar .. 6=Cumartesi
  const diff = day === 0 ? -6 : 1 - day;
  const monday = new Date(now);
  monday.setDate(now.getDate() + diff);
  monday.setHours(0, 0, 0, 0);
  return monday;
};

const Goller = () => {
  // --- SADECE GOOGLE GİRİŞİ ---
  const { currentUser, loginWithGoogle, logout } = useAuth();

  const userId = currentUser?.uid || null;
  const userEmail = currentUser?.email || null;
  const userDisplayName = currentUser?.displayName || '';

  const [showModal, setShowModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [videos, setVideos] = useState([]);
  const [filterCity, setFilterCity] = useState("");
  const [filterDistrict, setFilterDistrict] = useState("");

  const [newGoal, setNewGoal] = useState({
    name: '',
    pitchName: '',
    city: '',
    district: '',
    caption: '',
    consent: false
  });
  const [rotation, setRotation] = useState(0);

  // --- YORUMLAR ---
  const [openComments, setOpenComments] = useState({});   // { videoId: true }
  const [comments, setComments] = useState({});           // { videoId: [ ... ] }
  const [commentDraft, setCommentDraft] = useState({});   // { videoId: "..." }
  const commentUnsubs = useRef({});

  useEffect(() => {
    const subs = commentUnsubs.current;
    return () => {
      Object.values(subs).forEach((u) => u && u());
    };
  }, []);

  const toggleComments = (videoId) => {
    setOpenComments((prev) => {
      const next = { ...prev, [videoId]: !prev[videoId] };
      if (next[videoId] && !commentUnsubs.current[videoId]) {
        const q = query(collection(db, "goller", videoId, "comments"), orderBy("createdAt", "asc"));
        commentUnsubs.current[videoId] = onSnapshot(q, (snap) => {
          setComments((c) => ({ ...c, [videoId]: snap.docs.map((d) => ({ id: d.id, ...d.data() })) }));
        }, (err) => console.error("Yorum dinleme hatası:", err));
      } else if (!next[videoId] && commentUnsubs.current[videoId]) {
        commentUnsubs.current[videoId]();
        delete commentUnsubs.current[videoId];
      }
      return next;
    });
  };

  const addComment = async (videoId) => {
    if (!userId) return alert("Yorum yapmak için Google ile giriş yapmalısınız.");
    const text = (commentDraft[videoId] || '').trim();
    if (!text) return;
    try {
      await addDoc(collection(db, "goller", videoId, "comments"), {
        text: text.slice(0, 500),
        authorId: userId,
        authorName: userDisplayName || 'Oyuncu',
        authorPhoto: currentUser?.photoURL || '',
        createdAt: serverTimestamp()
      });
      await updateDoc(doc(db, "goller", videoId), { commentCount: increment(1) });
      setCommentDraft((d) => ({ ...d, [videoId]: '' }));

      const video = videos.find((v) => v.id === videoId);
      if (video && video.userId && video.userId !== userId) {
        pushNotification(video.userId, {
          type: 'comment',
          fromId: userId,
          fromName: userDisplayName || 'Bir oyuncu',
          fromPhoto: currentUser?.photoURL || '',
          targetId: videoId,
          text: text.slice(0, 120),
        });
      }
    } catch (e) {
      alert("Yorum eklenemedi: " + e.message);
    }
  };

  const deleteComment = async (videoId, commentId) => {
    try {
      await deleteDoc(doc(db, "goller", videoId, "comments", commentId));
      await updateDoc(doc(db, "goller", videoId), { commentCount: increment(-1) });
    } catch (e) {
      console.error("Yorum silinemedi:", e);
    }
  };

  // --- KİMLİK KONTROLÜ VE VERİ DİNLEME ---
  useEffect(() => {
    if (userId) {
      const emailVal = userEmail || '';
      const nameVal = userDisplayName || '';
      setDoc(doc(db, "users", userId), {
        email: emailVal,
        displayName: nameVal,
        lastLogin: serverTimestamp()
      }, { merge: true }).catch(e => console.error("User registration error:", e));
    }
  }, [userId, userEmail, userDisplayName]);

  useEffect(() => {
    // 2. Videoları gerçek zamanlı (Real-time) dinle
    const q = query(collection(db, "goller"), orderBy("createdAt", "desc"));
    const unsubDocs = onSnapshot(q, (snapshot) => {
      const now = new Date();

      const videoList = snapshot.docs.map(doc => ({
        id: doc.id,
        likes: 0,
        likedBy: [],
        ...doc.data()
      })).filter(v => {
        // Silinme tarihi kontrolü (Güvenli filtreleme)
        if (!v.silinmeTarihi) return true;
        return v.silinmeTarihi.toDate() > now;
      });

      setVideos(videoList);
    }, (error) => {
      console.error("Firebase Veri Çekme Hatası:", error);
    });

    return () => {
      unsubDocs();
    };
  }, []);

  const handleLogin = async () => {
    setAuthLoading(true);
    try {
      await loginWithGoogle();
    } catch (err) {
      alert("Google ile giriş başarısız oldu: " + (err.message || err));
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLike = async (videoId) => {
    const video = videos.find(v => v.id === videoId);
    if (!video) return;

    const videoRef = doc(db, "goller", videoId);
    const likedStorageKey = `liked_${videoId}`;
    const lastLikedTimeKey = `last_liked_at_${videoId}`;

    // --- DURUM KONTROLÜ ---
    // Eğer giriş yapmışsa DB'deki diziye bak, yapmamışsa LocalStorage'a bak
    const isLikedByMe = userId
      ? video.likedBy?.includes(userId)
      : localStorage.getItem(likedStorageKey);

    const lastLikedAt = localStorage.getItem(lastLikedTimeKey);
    const cooldownPeriod = 10 * 60 * 1000; // 10 Dakika Cooldown

    try {
      if (isLikedByMe) {
        // --- BEĞENİYİ GERİ AL ---
        await updateDoc(videoRef, {
          likes: Math.max(0, (video.likes || 1) - 1),
          likedBy: userId ? arrayRemove(userId) : (video.likedBy || [])
        });
        if (!userId) localStorage.removeItem(likedStorageKey);
      } else {
        // --- YENİ BEĞENİ ---

        // Giriş yapmayanlar için cooldown kontrolü
        if (!userId && lastLikedAt && (Date.now() - parseInt(lastLikedAt)) < cooldownPeriod) {
          const remainingMinutes = Math.ceil((cooldownPeriod - (Date.now() - parseInt(lastLikedAt))) / 60000);
          alert(`Ziyaretçi olarak çok hızlı beğeni yapıyorsun! Bu golü tekrar beğenmek için ${remainingMinutes} dakika beklemelisin. ⏳ (Giriş yaparsan bu sınırı aşabilirsin!)`);
          return;
        }

        await updateDoc(videoRef, {
          likes: (video.likes || 0) + 1,
          likedBy: userId ? arrayUnion(userId) : (video.likedBy || [])
        });

        if (!userId) {
          localStorage.setItem(likedStorageKey, "true");
          localStorage.setItem(lastLikedTimeKey, Date.now().toString());
        }

        if (userId && video.userId && video.userId !== userId) {
          pushNotification(video.userId, {
            type: 'like',
            fromId: userId,
            fromName: userDisplayName || 'Bir oyuncu',
            fromPhoto: currentUser?.photoURL || '',
            targetId: videoId,
          });
        }
      }
    } catch (e) {
      console.error("Like error:", e);
    }
  };

  const handleDelete = async (videoId) => {
    if (window.confirm("Bu golü silmek istediğine emin misin?")) {
      try {
        await deleteDoc(doc(db, "goller", videoId));
        alert("Gol başarıyla silindi.");
      } catch (e) {
        alert("Hata oluştu.");
      }
    }
  };

  // --- İZLENME SAYACI ---
  // Video ilk kez oynatıldığında "views" alanını +1 yapar.
  // Aynı tarayıcıda tekrar saymamak için localStorage ile sınırlar.
  const registerView = async (videoId) => {
    const key = `viewed_${videoId}`;
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, '1');
    try {
      await updateDoc(doc(db, "goller", videoId), { views: increment(1) });
    } catch (e) {
      console.error("İzlenme sayılamadı:", e);
    }
  };

  // İsimden avatar için baş harf
  const initialOf = (name) => (name || 'O').trim().charAt(0).toUpperCase();

  // --- YÜKLEME MOTORU (Cloudinary + Firebase) ---
  const handleUpload = async () => {
    if (!userId) return alert("Gol yüklemek için Google ile giriş yapmalısınız.");
    if (!selectedFile) return alert("Lütfen bir video dosyası seçin!");

    // Sadece video dosyalarına izin ver (Frontend Güvenliği)
    if (!selectedFile.type.startsWith("video/") && !selectedFile.type.startsWith("image/")) {
      return alert("Lütfen sadece video veya resim formatında bir dosya yükleyin!");
    }

    if (!newGoal.consent) {
      return alert("Devam etmek için bilgilendirme metnini kabul etmelisiniz.");
    }

    // 1. Engelli Kullanıcı Kontrolü
    const blockDoc = await getDoc(doc(db, "blockedUsers", userId));
    if (blockDoc.exists()) {
      alert("Üzgünüz, kuralları ihlal ettiğiniz için video yükleme yetkiniz askıya alınmıştır. ");
      return;
    }

    if (!newGoal.name && !userDisplayName) {
      return alert("Lütfen geçerli bir Ad Soyad giriniz!");
    }
    if (!newGoal.pitchName || newGoal.pitchName.length < 2) {
      return alert("Lütfen geçerli bir Halı Saha Adı giriniz!");
    }
    if (!newGoal.city) {
      return alert("Lütfen Şehir seçiniz!");
    }

    setUploading(true);

    try {
      const { url: mediaUrl } = await uploadMedia(selectedFile);

      const silinme = new Date();
      silinme.setDate(silinme.getDate() + 30);

      await addDoc(collection(db, "goller"), {
        name: newGoal.name || userDisplayName,
        pitchName: newGoal.pitchName,
        city: newGoal.city,
        district: newGoal.district,
        caption: (newGoal.caption || '').trim(),
        userEmail: userEmail,
        userId: userId,
        userPhoto: currentUser?.photoURL || '',
        src: mediaUrl,
        date: new Date().toLocaleDateString('tr-TR'),
        createdAt: serverTimestamp(),
        silinmeTarihi: Timestamp.fromDate(silinme),
        type: selectedFile.type.startsWith("image/") ? "image" : "video",
        rotation: rotation || 0,
        likes: 0,
        likedBy: [],
        views: 0,
        consent: true,
        consentAt: serverTimestamp()
      });

      setUploading(false);
      setShowModal(false);
      setSelectedFile(null);
      setRotation(0); // Rotasyonu sıfırla
      setNewGoal({ name: '', pitchName: '', city: '', district: '', caption: '', consent: false });
      alert("Golünüz başarı ile yüklendi! ");

    } catch (e) {
      console.error("Yükleme işlemi başarısız:", e);
      alert("Bir hata oluştu: " + e.message);
      setUploading(false);
    }
  };

  // Filtreleme mantığı - GÜVENLİ HALE GETİRİLDİ (Eski veriler için null kontrolü eklendi)
  const filteredVideos = videos.filter(v => {
    const city = v.city || "";
    const district = v.district || "";
    const cityMatch = city.toLowerCase().includes(filterCity.toLowerCase());
    const districtMatch = district.toLowerCase().includes(filterDistrict.toLowerCase());
    return cityMatch && districtMatch;
  });

  // HAFTANIN GOLÜ: sadece bu hafta (Pazartesi'den beri) yüklenenler arasında en çok beğenilen
  const weekStart = getWeekStart();
  const thisWeekVideos = videos.filter(v => {
    const created = v.createdAt?.toDate?.();
    return created && created >= weekStart;
  });
  const haftaninGolu = thisWeekVideos.length > 0
    ? [...thisWeekVideos].sort((a, b) => (b.likes || 0) - (a.likes || 0))[0]
    : null;

  const handleInputChange = (e) => {
    setNewGoal({ ...newGoal, [e.target.name]: e.target.value });
  };

  // Yorum paneli - hem "Haftanın Golü" hem gol kartları aynı paneli kullanır
  const renderComments = (video) => (
    <div className="comments-panel">
      <div className="comments-list">
        {(comments[video.id] || []).map((c) => (
          <div className="comment-item" key={c.id}>
            <img
              src={c.authorPhoto || 'https://via.placeholder.com/28'}
              alt=""
              className="comment-avatar"
            />
            <div className="comment-body">
              <span className="comment-author">{c.authorName}</span>
              <span className="comment-text">{c.text}</span>
            </div>
            {(userId === c.authorId || userId === video.userId) && (
              <button className="comment-del" onClick={() => deleteComment(video.id, c.id)}>
                <X size={12} />
              </button>
            )}
          </div>
        ))}
        {(comments[video.id] || []).length === 0 && (
          <p className="comments-empty">İlk yorumu sen yaz.</p>
        )}
      </div>
      <div className="comment-input-row">
        <input
          value={commentDraft[video.id] || ''}
          onChange={(e) => setCommentDraft((d) => ({ ...d, [video.id]: e.target.value }))}
          onKeyDown={(e) => { if (e.key === 'Enter') addComment(video.id); }}
          placeholder={userId ? "Yorum yaz..." : "Yorum için Google ile giriş yap"}
          maxLength={500}
        />
        <button onClick={() => addComment(video.id)} disabled={!userId}>Gönder</button>
      </div>
    </div>
  );

  return (
    <div className="market-wrapper">
      <nav className="market-navbar">
        <div className="nav-left">
          <Flame size={18} className="nav-left-icon" />
          <span>Gol Vitrini</span>
        </div>
        <div className="nav-right">
          {userId ? (
            <div className="d-flex align-items-center gap-3">
              <span className="text-white small d-none d-md-block">Hoş geldin, <b>{userDisplayName}</b></span>
              <button className="add-star-btn" onClick={() => setShowModal(true)}>
                <Plus size={16} /> Golünü Yükle
              </button>
              <button className="btn btn-link text-danger text-decoration-none p-0 small fw-bold" onClick={logout}>Çıkış</button>
            </div>
          ) : (
            <button className="add-star-btn" onClick={handleLogin} disabled={authLoading}>
              <Video size={16} /> {authLoading ? "Giriş yapılıyor..." : "Google ile Giriş / Gol Yükle"}
            </button>
          )}
        </div>
      </nav>

      <div className="market-content">
        <div className="page-header">
          <h1><Trophy className="page-header-icon" size={32} /> Haftanın Golleri</h1>
          <p>En iyi gollerini paylaş, haftanın yıldızı ol!</p>
        </div>

        <div className="search-bar-container">
          <select
            className="search-select"
            value={filterCity}
            onChange={(e) => { setFilterCity(e.target.value); setFilterDistrict(""); }}
            style={{ flex: 1, backgroundColor: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.1)', padding: '12px', borderRadius: '12px' }}
          >
            <option value="" style={{ backgroundColor: '#1a1a1a' }}>Şehir Seçin</option>
            {Object.keys(TURKEY_CITIES).map(city => (
              <option key={city} value={city} style={{ backgroundColor: '#1a1a1a' }}>{city}</option>
            ))}
          </select>

          <select
            className="search-select"
            value={filterDistrict}
            onChange={(e) => setFilterDistrict(e.target.value)}
            disabled={!filterCity}
            style={{ flex: 1, backgroundColor: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.1)', padding: '12px', borderRadius: '12px' }}
          >
            <option value="" style={{ backgroundColor: '#1a1a1a' }}>İlçe Seçin</option>
            {filterCity && TURKEY_CITIES[filterCity].map(dist => (
              <option key={dist} value={dist} style={{ backgroundColor: '#1a1a1a' }}>{dist}</option>
            ))}
          </select>

          <div className="d-flex align-items-center px-2 text-muted">
            <Search size={18} />
          </div>
        </div>

        {/* HAFTANIN GOLÜ */}
        {haftaninGolu && haftaninGolu.likes > 0 && !filterCity && !filterDistrict && (
          <div className="weekly-highlight">
            <div className="weekly-badge"> HAFTANIN EN İYİ GOLÜ </div>

            <div className="featured-player">
              {haftaninGolu.type === 'image' ? (
                <img
                  src={haftaninGolu.src}
                  alt="Haftanın Golü"
                  style={{ transform: `rotate(${haftaninGolu.rotation || 0}deg)` }}
                />
              ) : (
                <video
                  src={haftaninGolu.src}
                  controls
                  playsInline
                  onPlay={() => registerView(haftaninGolu.id)}
                  style={{ transform: `rotate(${haftaninGolu.rotation || 0}deg)` }}
                />
              )}
              <span className="player-watermark">sahalive.com</span>
            </div>

            <div className="featured-body">
              <div className="goal-actions">
                <button
                  className={`like-btn ${((userId && haftaninGolu.likedBy?.includes(userId)) || localStorage.getItem(`liked_${haftaninGolu.id}`)) ? 'active' : ''}`}
                  onClick={() => handleLike(haftaninGolu.id)}
                >
                  <Heart
                    size={22}
                    fill={((userId && haftaninGolu.likedBy?.includes(userId)) || localStorage.getItem(`liked_${haftaninGolu.id}`)) ? "#e74c3c" : "none"}
                    color="#e74c3c"
                  />
                  <span>{haftaninGolu.likes || 0}</span>
                </button>
                <button className="comment-toggle-btn" onClick={() => toggleComments(haftaninGolu.id)}>
                  <MessageCircle size={20} />
                  <span>{haftaninGolu.commentCount || 0}</span>
                </button>
                <span className="view-count">{haftaninGolu.views || 0} kişi izledi · {haftaninGolu.date}</span>
              </div>

              <div className="goal-author-row">
                {haftaninGolu.userPhoto ? (
                  <img src={haftaninGolu.userPhoto} alt="" />
                ) : (
                  <span className="avatar-fallback">{initialOf(haftaninGolu.name)}</span>
                )}
                <div className="goal-author-info">
                  <span className="goal-author-name">{haftaninGolu.name}</span>
                  <span className="goal-author-meta">
                    <MapPin size={13} /> {haftaninGolu.pitchName} · {haftaninGolu.city}{haftaninGolu.district ? ` / ${haftaninGolu.district}` : ''}
                  </span>
                </div>
              </div>

              {haftaninGolu.caption && (
                <p className="goal-caption">{haftaninGolu.caption}</p>
              )}

              {openComments[haftaninGolu.id] && renderComments(haftaninGolu)}
            </div>
          </div>
        )}

        <div className="cards-grid">
          {filteredVideos.map((video) => (
            <div key={video.id} className="pro-card">
              {userId && userId === video.userId && (
                <div className="delete-icon" onClick={() => handleDelete(video.id)}>
                  <Trash2 size={18} />
                </div>
              )}

              <div className="video-container">
                {video.type === 'image' ? (
                  <img
                    src={video.src}
                    alt="Gol"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      transform: `rotate(${video.rotation || 0}deg)`
                    }}
                  />
                ) : (
                  <video
                    src={video.src}
                    controls
                    playsInline
                    preload="metadata"
                    onPlay={() => registerView(video.id)}
                    style={{ transform: `rotate(${video.rotation || 0}deg)` }}
                  />
                )}
                <span className="player-watermark">sahalive.com</span>
              </div>

              <div className="card-body">
                <div className="goal-author-row">
                  {video.userPhoto ? (
                    <img src={video.userPhoto} alt="" />
                  ) : (
                    <span className="avatar-fallback">{initialOf(video.name)}</span>
                  )}
                  <div className="goal-author-info">
                    <span className="goal-author-name">{video.name}</span>
                    <span className="goal-author-meta">
                      <MapPin size={13} /> {video.pitchName || "Halı Saha"} · {video.city}{video.district ? ` / ${video.district}` : ''}
                    </span>
                  </div>
                </div>

                {video.caption && (
                  <p className="goal-caption">{video.caption}</p>
                )}

                <div className="goal-actions">
                  <button
                    className={`like-btn ${((userId && video.likedBy?.includes(userId)) || localStorage.getItem(`liked_${video.id}`)) ? 'active' : ''}`}
                    onClick={() => handleLike(video.id)}
                  >
                    <Heart
                      size={20}
                      fill={((userId && video.likedBy?.includes(userId)) || localStorage.getItem(`liked_${video.id}`)) ? "#e74c3c" : "none"}
                      stroke="#e74c3c"
                    />
                    <span>{video.likes || 0}</span>
                  </button>
                  <button className="comment-toggle-btn" onClick={() => toggleComments(video.id)}>
                    <MessageCircle size={18} />
                    <span>{video.commentCount || 0}</span>
                  </button>
                  <span className="view-count">{video.views || 0} izlenme · {video.date}</span>
                </div>

                {openComments[video.id] && renderComments(video)}
              </div>
            </div>
          ))}
        </div>

        {filteredVideos.length === 0 && (
          <div className="py-5 text-muted">
            <Video size={48} className="mb-3 opacity-25" />
            <p className="fw-bold">Burada henüz gol yok. İlk bombayı sen patlat!</p>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-box">
            <button className="close-btn" onClick={() => setShowModal(false)}>
              <X size={24} />
            </button>

            <h2>Golünü Paylaş </h2>
            <p>Videonun altına ismini ve sahanı yazmayı unutma!</p>

            <form onSubmit={(e) => { e.preventDefault(); handleUpload(); }}>
              <div className="form-group">
                <label>Ad Soyad</label>
                <input
                  type="text"
                  name="name"
                  value={newGoal.name}
                  onChange={handleInputChange}
                  placeholder="Kralın Adı Soyadı"
                  required
                />
              </div>

              <div className="form-group">
                <label>Halı Saha Adı</label>
                <input
                  type="text"
                  name="pitchName"
                  value={newGoal.pitchName}
                  onChange={handleInputChange}
                  placeholder="Örn: Şampiyon Halı Saha"
                  required
                />
              </div>

              <div className="form-group">
                <label>Açıklama <span style={{ color: '#95a5a6', fontWeight: 400 }}>(isteğe bağlı)</span></label>
                <textarea
                  name="caption"
                  value={newGoal.caption}
                  onChange={handleInputChange}
                  placeholder="Bu golü nasıl attın? Birkaç kelime yaz..."
                  maxLength={200}
                  rows={2}
                  style={{ resize: 'vertical' }}
                />
              </div>

              <div className="form-row-split" style={{ display: 'flex', gap: '15px' }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Şehir</label>
                  <select
                    name="city"
                    value={newGoal.city}
                    onChange={(e) => setNewGoal({ ...newGoal, city: e.target.value, district: "" })}
                    className="form-control"
                    required
                  >
                    <option value="">Seçiniz</option>
                    {Object.keys(TURKEY_CITIES).map(city => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>İlçe</label>
                  <select
                    name="district"
                    value={newGoal.district}
                    onChange={handleInputChange}
                    className="form-control"
                    disabled={!newGoal.city}
                  >
                    <option value="">Seçiniz</option>
                    {newGoal.city && TURKEY_CITIES[newGoal.city]?.map(dist => (
                      <option key={dist} value={dist}>{dist}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Video Dosyası</label>
                <input
                  type="file"
                  accept="video/*,image/*"
                  onChange={(e) => {
                    setSelectedFile(e.target.files[0]);
                    setRotation(0); // Yeni dosya seçildiğinde rotasyonu sıfırla
                  }}
                  className="form-control"
                  disabled={uploading}
                  required
                />
              </div>

              {selectedFile && (
                <div className="video-preview-upload">
                  <div className="preview-container">
                    {selectedFile.type.startsWith("image/") ? (
                      <img 
                        src={URL.createObjectURL(selectedFile)} 
                        alt="Önizleme"
                        style={{ transform: `rotate(${rotation}deg)` }} 
                      />
                    ) : (
                      <video 
                        src={URL.createObjectURL(selectedFile)} 
                        style={{ transform: `rotate(${rotation}deg)` }} 
                      />
                    )}
                  </div>
                  <button 
                    type="button" 
                    className="rotate-btn"
                    onClick={() => setRotation(prev => (prev + 90) % 360)}
                  >
                    <RotateCw size={16} /> Videoyu Döndür ({rotation}°)
                  </button>
                </div>
              )}

              <label className="goller-consent">
                <input
                  type="checkbox"
                  checked={newGoal.consent}
                  onChange={(e) => setNewGoal({ ...newGoal, consent: e.target.checked })}
                />
                <span>
                  Yüklediğim videonun/görselin <b>bana ait olduğunu</b>, üçüncü kişilerin haklarını
                  ihlal etmediğini ve SahaLive'da yayınlanmasını kabul ediyorum. İçerikten doğabilecek
                  sorumluluk bana aittir; SahaLive yalnızca aracı platformdur.
                </span>
              </label>

              {uploading ? (
                <div className="text-center py-3">
                  <div className="spinner-border text-warning mb-2" role="status"></div>
                  <p className="fw-bold text-dark">İşlem yapılıyor, lütfen bekleyin...</p>
                </div>
              ) : (
                <button type="submit" className="save-modal-btn">
                  Golü Sahaya İndir!
                </button>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Goller;